import React, { useMemo } from "react";
import { AsyncSelectPickerInput, Button, SelectPickerInput } from "@shared/components";
import { changeListeners, FieldErrorMessage, formSubmitHandler, schemaValidators, useAppForm } from "@shared/form";
import { Team, User } from "@shared/models";
import { CreateUserDto, UserFormShape } from "@containers/User/interfaces/UserForm.interface";

import UserFormTextInput from "./components/UserFormTextInput/UserFormTextInput";
import {
  getInitValues,
  validationSchema,
  prepareSubmitValue,
  handlers,
  USER_CONNECTION_OPTIONS,
  USER_TYPE_EMPLOYMENT_OPTIONS,
} from "./formHelpers";
import "./index.scss";

/** The footer sits outside the `<form>`, so its submit button points at the form by this id. */
const USER_FORM_ID = "user-edit-form";

interface UserFormProps {
  user: User | null;
  teams: Team[];
  onChangeForm: () => void;
  onCloseForm: () => void;
  onSubmitForm: (payload: CreateUserDto) => void;
}

interface UserFormFieldsProps extends UserFormProps {
  formValues: UserFormShape;
}

/** A picker in its bordered box, with the field's error as a sibling below it. */
const PickerWithError: React.FunctionComponent<React.PropsWithChildren> = ({ children }) => (
  <div className="user-select-input-wrapper">
    <div className="user-select-input">{children}</div>
    <FieldErrorMessage />
  </div>
);

/** The form body: owns the form instance, built once per set of initial values (see `UserEditForm`). */
const UserEditFormFields: React.FunctionComponent<UserFormFieldsProps> = (props) => {
  const { user, teams, formValues, onChangeForm, onCloseForm, onSubmitForm } = props;

  const teamOptions = useMemo(() => teams.map((t) => ({ label: t.name, value: String(t.id) })), [teams]);

  const form = useAppForm({
    defaultValues: formValues,
    validators: schemaValidators(validationSchema),
    listeners: changeListeners(onChangeForm),
    onSubmit: ({ value }) => {
      onSubmitForm(prepareSubmitValue(validationSchema.parse(value), user));
    },
  });

  return (
    <div className="user-form">
      <form id={USER_FORM_ID} onSubmit={formSubmitHandler(form)}>
        <div className="user-form-content">
          <div className="text-input-group">
            <form.AppField name="first_name">
              {() => <UserFormTextInput labelTitle={"First name"} placeholder={"First name"} />}
            </form.AppField>
            <form.AppField name="last_name">
              {() => <UserFormTextInput labelTitle={"Last name"} placeholder={"Last name"} />}
            </form.AppField>
            <form.AppField name="email">
              {() => <UserFormTextInput labelTitle={"Email"} placeholder={"Email"} />}
            </form.AppField>
            <form.AppField name="job_title">
              {() => <UserFormTextInput labelTitle={"Job Title"} placeholder={"Job Title"} />}
            </form.AppField>
          </div>
          {/* Each picker and its error are siblings (the error sits outside the bordered picker box), so the
              pickers are bound by hand instead of through `field.Select` / `field.AsyncSelect`, which render the
              error inside. */}
          <form.AppField name="permission">
            {(field) => (
              <PickerWithError>
                <AsyncSelectPickerInput
                  searchable={false}
                  labelTitle="Permission Level"
                  name={field.name}
                  className="user-permission-input"
                  onChange={(option) => field.handleChange(option)}
                  value={field.state.value}
                  placeholder="Select Permission Level"
                  getData={handlers.roles.getData}
                  selectData={handlers.roles.selectData}
                  prepareOptionFunction={handlers.roles.prepareOptionFunction}
                  loadingAction={handlers.roles.loadingAction}
                />
              </PickerWithError>
            )}
          </form.AppField>
          <div>
            <div className="teams-header">Team</div>
            <form.AppField name="team">
              {(field) => (
                <>
                  <div className="teams-body">
                    {teamOptions.map((teamOption) => (
                      <label className="team-label" key={teamOption.value}>
                        <input
                          name={teamOption.label}
                          type="radio"
                          className="team-radio"
                          value={teamOption.value}
                          checked={field.state.value?.value === teamOption.value}
                          onChange={() => field.handleChange(teamOption)}
                        />
                        <div className="team-label-text">{teamOption.label}</div>
                      </label>
                    ))}
                  </div>
                  <FieldErrorMessage />
                </>
              )}
            </form.AppField>
          </div>
          <form.AppField name="connection">
            {(field) => (
              <PickerWithError>
                <SelectPickerInput
                  searchable={false}
                  name={field.name}
                  cleanable={false}
                  data={USER_CONNECTION_OPTIONS}
                  onChange={(value) => field.handleChange(value)}
                  labelTitle="Connection"
                  value={field.state.value}
                  placeholder="Connection"
                />
              </PickerWithError>
            )}
          </form.AppField>
          <form.AppField name="type_employment">
            {(field) => (
              <PickerWithError>
                <SelectPickerInput
                  searchable={false}
                  name={field.name}
                  cleanable={false}
                  data={USER_TYPE_EMPLOYMENT_OPTIONS}
                  onChange={(value) => field.handleChange(value)}
                  labelTitle="Type of employment"
                  value={field.state.value}
                  placeholder="Type of employment"
                />
              </PickerWithError>
            )}
          </form.AppField>
          <form.AppField name="manager">
            {(field) => (
              <PickerWithError>
                <AsyncSelectPickerInput
                  labelTitle="Reports to"
                  name={field.name}
                  className="manager-input"
                  onChange={(option) => field.handleChange(option)}
                  value={field.state.value}
                  placeholder="Select Manager"
                  getData={handlers.manager.getData}
                  selectData={handlers.manager.selectData}
                  prepareOptionFunction={handlers.manager.prepareOptionFunction}
                  loadingAction={handlers.manager.loadingAction}
                  onSort={handlers.manager.onSort}
                />
              </PickerWithError>
            )}
          </form.AppField>
          <div className="user-date-input-wrapper">
            <form.AppField name="start_date">
              {(field) => (
                <field.Input type="date" className="date-input" labelTitle="Start Date" placeholder="Start Date" />
              )}
            </form.AppField>
          </div>
          <div className="user-date-input-wrapper">
            <form.AppField name="used_pto">
              {(field) => (
                <field.Input
                  type="number"
                  className="date-input"
                  labelTitle="Used PTO"
                  min={0}
                  max={100}
                  placeholder="Used PTO"
                />
              )}
            </form.AppField>
          </div>
        </div>
      </form>

      <div className="user-form-footer">
        <Button type="button" className="close-btn margin-right" onClick={() => onCloseForm()}>
          Cancel
        </Button>
        <Button type="submit" form={USER_FORM_ID} className="accent padding">
          {user ? "Update Teammate" : "Add Teammate"}
        </Button>
      </div>
    </div>
  );
};

/** Keyed on the user's initial values: a user that arrives or changes while the form is open mounts a fresh form. */
const UserEditForm: React.FunctionComponent<UserFormProps> = (props) => {
  const formValues = useMemo(() => getInitValues(props.user), [props.user]);

  return <UserEditFormFields key={JSON.stringify(formValues)} formValues={formValues} {...props} />;
};

export default UserEditForm;
